import { useMemo } from 'react';

import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { MergeRecordsModal } from '@/components/modals/MergeRecordsModal';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';

import { useMergeClientProfilesMutation } from './MergeClientProfiles.generated';

export interface MergeProfileOption {
  id: string;
  displayName: string;
}

interface MergeProfilesForm {
  keepId: string;
}

export function MergeClientProfilesModal({
  profiles,
  remove,
  onClose,
  onMerged,
}: {
  profiles: MergeProfileOption[];
  remove: MergeProfileOption;
  onClose: () => void;
  onMerged: () => void;
}) {
  const { control, watch, handleSubmit, formState } =
    useForm<MergeProfilesForm>({
      defaultValues: { keepId: '' },
    });
  const keepId = watch('keepId');
  const loading = formState.isSubmitting;
  const { createSuccessFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [merge, { error }] = useMergeClientProfilesMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onError: (error) => reportError('Could not merge client profiles', error),
    onCompleted: () => {
      createSuccessFeedback('People merged successfully.')();
      onMerged();
    },
  });
  const options = useMemo(
    () =>
      profiles
        .filter((profile) => profile.id !== remove.id)
        .map((profile) => ({
          value: profile.id,
          display: profile.displayName,
        })),
    [profiles, remove.id]
  );

  const handleMerge = handleSubmit(
    async ({ keepId }) => {
      if (loading) return;
      await merge({
        variables: {
          input: {
            keepClientProfileID: keepId,
            removeClientProfileID: remove.id,
          },
        },
        update: (cache) => {
          cache.evict({
            id: cache.identify({ __typename: 'ClientProfile', id: remove.id }),
          });
          cache.gc();
        },
      });
    },
    () => undefined
  );

  return (
    <MergeRecordsModal
      heading="Merge people"
      subheading={`Merge “${remove.displayName}” with another person.`}
      duplicateName={remove.displayName}
      pending={loading}
      disabled={!keepId}
      onClose={onClose}
      onConfirm={handleMerge}
      moved="Connected records from the duplicate person merge into the selected person"
      discarded="Conflicting details may be discarded"
      retainedHeading="Selected person"
      retained="Their details take priority"
      destinationInput={
        <FormAwareTypeaheadSelectInput
          label="Merges into"
          placeholder="Select a person"
          control={control}
          fieldName="keepId"
          required
          options={options}
          disabled={loading}
        />
      }
    >
      {error && (
        <Callout severity="error" role="alert">
          Could not merge these people. They may have conflicting records, or
          your access may have changed. Refresh the page before trying again.
        </Callout>
      )}
    </MergeRecordsModal>
  );
}
